import Icon from '@mui/material/Icon';

import Info from '@mui/icons-material/Info';
import Event from '@mui/icons-material/Event';
import EventBusy from '@mui/icons-material/EventBusy';
import Report from '@mui/icons-material/Report';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Cancel from '@mui/icons-material/Cancel';
import AccessTime from '@mui/icons-material/AccessTime';
import WorkHistory from '@mui/icons-material/WorkHistory';

import { blue, green, orange, purple, red, yellow } from '@mui/material/colors';

import { DateTime } from 'luxon';

/**
 * Format timestamp to readable date/time string
 * @param {number|string} timestamp - Unix timestamp in milliseconds or ISO string
 * @returns {string} Formatted date string
 */
export const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';

    try {
        const dt = typeof timestamp === 'number'
            ? DateTime.fromMillis(timestamp)
            : DateTime.fromISO(timestamp);

        return dt.toLocaleString(DateTime.DATETIME_MED);
    } catch (error) {
        return 'Invalid Date';
    }
};

/**
 * Mask sensitive email addresses
 * @param {string} email - Email address to mask
 * @returns {string} Masked email
 */
export const maskEmail = (email) => {
    if (!email) return 'N/A';

    const [ local, domain ] = email.split('@');
    if (!local || !domain) return email;

    const maskedLocal = local.length > 2
        ? `${ local.slice(0, 2) }***`
        : `${ local[ 0 ] }***`;

    return `${ maskedLocal }@${ domain }`;
};

/**
 * Mask phone numbers
 * @param {string} phone - Phone number to mask
 * @returns {string} Masked phone number
 */
export const maskPhone = (phone) => {
    if (!phone) return 'N/A';

    // Keep first 3 and last 2 digits
    if (phone.length > 5) {
        return `${ phone.slice(0, 3) }***${ phone.slice(-2) }`;
    }

    return '***';
};

/**
 * Format boolean to checkmark/X
 * @param {boolean} value - Boolean value
 * @returns {string} ✓ or ✗
 */
export const formatBoolean = (value) => {
    return value ? '✓' : '✗';
};

/**
 * Format notification types array to readable string
 * @param {Array} notifications - Array of notification types
 * @returns {string} Formatted notification list
 */
export const formatNotifications = (notifications) => {
    if (!notifications || !Array.isArray(notifications)) return 'None';

    return notifications.join(', ');
};

/**
 * Format duration in seconds to human readable
 * @param {string|number} seconds - Duration in seconds
 * @returns {string} Human readable duration
 */
export const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';

    const duration = parseInt(seconds.toString().replace('s', ''));
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const secs = duration % 60;

    if (hours > 0) {
        return `${ hours }h ${ minutes }m`;
    } else if (minutes > 0) {
        return `${ minutes }m ${ secs }s`;
    } else {
        return `${ secs }s`;
    }
};

/**
 * Mask message ID for display
 * @param {string} id - Full message ID
 * @returns {string} Masked ID
 */
export const maskId = (id) => {
    if (!id) return 'N/A';

    return id.length > 8 ? `${ id.slice(0, 8) }...` : id;
};

/**
 * Get color based on preference value
 * @param {string} pref - preference value
 * @returns {string} MUI color name
 */
export const getPrefColor = (pref) => {
    const preferenceColors = {
        missedShift: orange[500],
        shiftReminder: blue[600],
        shiftStart: green[500],
        chairLiftEvac: red[500],
        urgentRequest: purple[500],
        cancelledShift: yellow[200]
    };

    return preferenceColors[ pref ] || 'default';
};

export const getPrefIcon = (pref) => {
    const preferenceIcons = {
        missedShift: <Icon component={ Cancel } fontSize="small" />,
        shiftReminder: <Icon component={ AccessTime } fontSize="small" />,
        shiftStart: <Icon component={ CheckCircle } fontSize="small" />,
        chairLiftEvac: <Icon component={ Report } fontSize="small" />,
        urgentRequest: <Icon component={ WorkHistory } fontSize="small" />,
        cancelledShift: <Icon component={ EventBusy } fontSize="small" />
    };

    return preferenceIcons[ pref ] || <Icon component={ Info } fontSize="small" />;
};

/**
 * Get status color based on status value
 * @param {string} status - Status value
 * @returns {string} MUI color name
 */
export const getStatusColor = (status) => {
    const statusColors = {
        pending: 'warning',
        processing: 'info',
        sent: 'success',
        failed: 'error',
        upcoming: 'default',
        active: 'info',
        completed: 'success'
    };

    return statusColors[ status ] || 'default';
};
